// frontend/src/ProtectedRoute.tsx - Route Protection Component

import React from 'react';
import { useAuth } from './AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'ADMIN' | 'PROCESS_OWNER' | 'EVALUATION_PANEL_MEMBER' | 'AUDITOR' | 'SUPER_ADMIN';
  fallback?: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole }) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return null; // AuthContext will redirect to login
  }

  // Check role-based access
  if (requiredRole) {
    const hasAccess = checkRoleAccess(user.role, requiredRole);
    if (!hasAccess) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-4">🚫</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-4">
              You don't have permission to access this page.
            </p>
            <p className="text-sm text-gray-500">
              Required role: {requiredRole} | Your role: {user.role}
            </p>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
};

// Helper function to check role hierarchy and access
const checkRoleAccess = (userRole: string, requiredRole: string): boolean => {
  const roleHierarchy = {
    'SUPER_ADMIN': 5,
    'ADMIN': 4,
    'PROCESS_OWNER': 3,
    'EVALUATION_PANEL_MEMBER': 2,
    'AUDITOR': 1
  };

  const userLevel = roleHierarchy[userRole as keyof typeof roleHierarchy] || 0;
  const requiredLevel = roleHierarchy[requiredRole as keyof typeof roleHierarchy] || 0;

  return userLevel >= requiredLevel;
};

export default ProtectedRoute;